import React, { useEffect, useState } from "react";
import {
  BarChart3,
  ShoppingCart,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Clock,
  Users,
  Download,
  Check,
} from "lucide-react";
import PageWrapper from "components/PageWrapper";
import Spinner from "components/Spinner";
import { getRecentBomAPI } from "actions/bomAnalysisActions";
import { buildBomExcelData, getOptimalBomItemResults } from "utils/bomToolUtils";

const RANK_LABELS = ["Primary", "Secondary", "Alternative"];
const RANK_TONES = [
  { circle: "bg-s360-success-bg text-s360-success-ink", bar: "bg-s360-success" },
  { circle: "bg-s360-info-bg text-[#002E4D]", bar: "bg-s360-info" },
  { circle: "bg-s360-warning-bg text-s360-warning-ink", bar: "bg-s360-warning" },
];

const DIST_WM = {
  Mouser: "bg-[#005DAA]",
  Digikey: "bg-[#CC0000]",
  Element14: "bg-[#FF6600]",
  "Element 14": "bg-[#FF6600]",
  Arrow: "bg-[#0B7D72]",
};
const wmShort = (name) => (name || "?").replace(/[^a-zA-Z0-9]/g, "").slice(0, 3);
const wmColor = (name) => DIST_WM[name] || "bg-s360-violet";

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const BomAnalysisPage = () => {
  const [all, setAll] = useState({
    bomItems: [],
    excelData: { distributors: [], rows: [], distributorSummary: [], optimalGrandTotal: 0 },
    loading: true,
  });

  const onLoad = async () => {
    const res = await getRecentBomAPI();
    console.log("BOM Analysis Response----->", { res });
    if (res.status !== true) return;
    const bomItems = getOptimalBomItemResults(res.data?.bom_items);
    const excelData = buildBomExcelData(res.data?.bom_items, bomItems);
    setAll({ bomItems, excelData, loading: false });
  };

  useEffect(() => {
    onLoad();
  }, []);

  const { distributors, rows, distributorSummary, optimalGrandTotal } = all.excelData;
  const sortedSummary = [...(distributorSummary ?? [])].sort(
    (a, b) => a.totalPrice - b.totalPrice,
  );
  const totalUnavailable = sortedSummary.reduce((s, d) => s + d.unavailableCount, 0);

  // Only distributors that can fulfill the entire BOM are candidates for
  // "cheapest single source". Rank fully-covering first, then partial.
  const fullyCovering = sortedSummary.filter((d) => d.unavailableCount === 0);
  const partialOnly = sortedSummary.filter((d) => d.unavailableCount > 0);
  const ranked = [...fullyCovering, ...partialOnly];
  const cheapestFull = fullyCovering[0];
  const cheapestSingle = cheapestFull?.totalPrice ?? 0;
  const savings = cheapestSingle > 0 ? cheapestSingle - optimalGrandTotal : 0;
  const savingsPct = cheapestSingle > 0 ? (savings / cheapestSingle) * 100 : 0;
  const bestAvail = ranked[0] ?? sortedSummary[0];
  const bestAvailPct = bestAvail
    ? Math.round(
        (bestAvail.availableCount /
          (bestAvail.availableCount + bestAvail.unavailableCount || 1)) *
          100,
      )
    : 0;

  return (
    <PageWrapper>
      <div className="flex flex-col gap-4">
        {/* Page head */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
              BOM analysis
            </h1>
            <span className="text-sm text-s360-fg3">
              {rows.length} parts analyzed across {distributors.length} distributors
            </span>
          </div>
          <div className="flex gap-2">
            <button className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2">
              <Download className="w-3.5 h-3.5" /> Export
            </button>
            <button className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md bg-s360-violet text-white text-[13px] font-semibold hover:bg-s360-violet-600">
              <ShoppingCart className="w-3.5 h-3.5" /> Create order
            </button>
          </div>
        </div>

        {all.loading ? (
          <div className="py-16">
            <Spinner label="Loading BOM data..." />
          </div>
        ) : (
          <>
            {/* Hero strip */}
            <div
              className="rounded-lg shadow-md grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-white/10 overflow-hidden"
              style={{
                background:
                  "linear-gradient(135deg, #191334 0%, #2b1d6e 100%)",
              }}
            >
              <div className="px-5 py-4 flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-s360-teal">
                  {cheapestFull ? "Estimated savings · optimized mix" : "Optimized mix required"}
                </span>
                <span className="font-mono text-[28px] leading-9 font-bold text-white tabular-nums">
                  {cheapestFull ? fmt(savings) : fmt(optimalGrandTotal)}
                </span>
                <span className="text-xs text-white/65">
                  {cheapestFull ? (
                    <>
                      <span className="text-s360-teal font-semibold">↓ {savingsPct.toFixed(1)}%</span>{" "}
                      vs {cheapestFull.distributor} (cheapest full-coverage source)
                    </>
                  ) : (
                    <>No single distributor can fulfill this BOM — split sourcing required</>
                  )}
                </span>
              </div>
              <div className="px-5 py-4 flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-s360-teal">
                  Optimal total
                </span>
                <span className="font-mono text-[28px] leading-9 font-bold text-white tabular-nums">
                  {fmt(optimalGrandTotal)}
                </span>
                <span className="text-xs text-white/65">
                  across all {distributors.length} distributors
                </span>
              </div>
              <div className="px-5 py-4 flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-s360-teal">
                  Best availability
                </span>
                <span className="font-mono text-[28px] leading-9 font-bold text-white tabular-nums">
                  {bestAvailPct}%
                </span>
                <span className="text-xs text-white/65">
                  {bestAvail?.distributor} · {bestAvail?.availableCount} of {rows.length} parts
                </span>
              </div>
              <div className="px-5 py-4 flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-s360-teal">
                  Coverage gaps
                </span>
                <span className="font-mono text-[28px] leading-9 font-bold text-white tabular-nums">
                  {totalUnavailable}
                </span>
                <span className="text-xs text-white/65">missing distributor offers</span>
              </div>
            </div>

            {/* Price matrix */}
            <div className="bg-white rounded-md shadow-sm flex flex-col overflow-hidden">
              <div className="px-[18px] py-3.5 border-b border-s360-stroke1 flex items-center gap-3 flex-wrap">
                <h3 className="text-base font-semibold text-s360-fg1 m-0">
                  Parts × distributor price matrix
                </h3>
                <span className="text-xs text-s360-fg3">
                  best price per row highlighted · grand totals below
                </span>
                <button className="ml-auto inline-flex items-center gap-1.5 h-[30px] px-3 rounded-md bg-white border border-s360-stroke1 text-xs font-semibold text-s360-fg1 hover:bg-s360-bg2">
                  <BarChart3 className="w-3.5 h-3.5" /> Export analysis
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="text-left px-3.5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap">
                        Part number
                      </th>
                      <th className="text-right px-3.5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap">
                        Req qty
                      </th>
                      {distributors.map((dist) => (
                        <th
                          key={dist}
                          className="text-left px-3.5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap"
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className={`w-[22px] h-[22px] rounded-[4px] inline-flex items-center justify-center text-[9px] font-bold text-white ${wmColor(dist)}`}
                            >
                              {wmShort(dist)}
                            </span>
                            {dist}
                          </span>
                        </th>
                      ))}
                      <th className="text-left px-3.5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap">
                        Recommended
                      </th>
                      <th className="text-right px-3.5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap">
                        Optimal total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => {
                      const prices = distributors
                        .map((d) => row.distPrices[d]?.totalPrice)
                        .filter((v) => v != null && v > 0);
                      const bestTotal = prices.length > 0 ? Math.min(...prices) : null;
                      return (
                        <tr key={idx} className="border-b border-s360-bg2 hover:bg-s360-stripe">
                          <td className="px-3.5 py-3 font-mono text-[13px] font-semibold text-s360-violet whitespace-nowrap">
                            {row.part_number}
                          </td>
                          <td className="px-3.5 py-3 text-right font-mono text-[13px] text-s360-fg1 tabular-nums">
                            {row.qty}
                          </td>
                          {distributors.map((dist) => {
                            const dp = row.distPrices[dist];
                            const isBest =
                              dp != null && bestTotal != null && dp.totalPrice === bestTotal;
                            const noStock = dp == null || dp.avail === 0;
                            return (
                              <td
                                key={dist}
                                className={`px-3.5 py-3 ${
                                  isBest ? "bg-[#F2FBF5]" : noStock ? "bg-s360-danger-bg/40" : ""
                                }`}
                              >
                                {dp != null ? (
                                  <div className="flex flex-col">
                                    <span
                                      className={`font-mono text-[13px] tabular-nums ${
                                        isBest ? "font-bold text-s360-success-ink" : "text-s360-fg1"
                                      }`}
                                    >
                                      {fmt(dp.totalPrice)}
                                    </span>
                                    <span className="text-[11px] text-s360-fg3">
                                      MOQ {dp.slab?.min_qty ?? "-"}
                                    </span>
                                    {dp.avail === 0 ? (
                                      <span className="text-[11px] font-medium text-s360-danger">
                                        No stock
                                      </span>
                                    ) : (
                                      <span className="text-[11px] text-s360-success">
                                        stock {Number(dp.avail).toLocaleString()}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-[11px] font-medium text-s360-danger">
                                    No stock
                                  </span>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-3.5 py-3">
                            {row.optimalDistributor ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-s360-violet-50 text-s360-violet-900 text-xs font-semibold">
                                <span
                                  className={`w-[18px] h-[18px] rounded-[3px] inline-flex items-center justify-center text-[8px] font-bold text-white ${wmColor(row.optimalDistributor)}`}
                                >
                                  {wmShort(row.optimalDistributor)}
                                </span>
                                {row.optimalDistributor}
                              </span>
                            ) : (
                              <span className="text-s360-fg-disabled">—</span>
                            )}
                          </td>
                          <td className="px-3.5 py-3 text-right font-mono text-[13px] font-bold text-s360-ink tabular-nums">
                            {row.optTotalPrice != null ? fmt(row.optTotalPrice) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-s360-violet-50 border-t-2 border-s360-violet-100">
                      <td colSpan={2} className="px-3.5 py-3 text-[13px] font-bold text-s360-violet-900">
                        Grand total
                      </td>
                      {distributors.map((dist) => {
                        const ds = distributorSummary.find((d) => d.distributor === dist);
                        return (
                          <td
                            key={dist}
                            className="px-3.5 py-3 font-mono text-[13px] font-bold text-s360-ink tabular-nums"
                          >
                            {ds ? fmt(ds.totalPrice) : "—"}
                          </td>
                        );
                      })}
                      <td />
                      <td className="px-3.5 py-3 text-right font-mono text-[13px] font-bold text-s360-success tabular-nums">
                        {fmt(optimalGrandTotal)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Supplier recommendations + alerts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              {/* Supplier recommendations */}
              <div className="bg-white rounded-md shadow-sm flex flex-col">
                <div className="px-[18px] py-3.5 border-b border-s360-stroke1">
                  <h3 className="text-base font-semibold text-s360-fg1 m-0">
                    Supplier recommendations
                  </h3>
                  <p className="text-xs text-s360-fg3 mt-0.5">
                    Sourcing the entire BOM from one distributor — fully-covering sources ranked first
                  </p>
                </div>
                <div className="flex flex-col divide-y divide-s360-bg2">
                  {ranked.slice(0, 3).map((d, i) => {
                    const total = d.availableCount + d.unavailableCount;
                    const availPct = total > 0 ? Math.round((d.availableCount / total) * 100) : 0;
                    const tone = RANK_TONES[i] ?? RANK_TONES[2];
                    return (
                      <div key={d.distributor} className="flex items-center gap-3 px-[18px] py-3">
                        <span
                          className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-[13px] font-bold shrink-0 ${tone.circle}`}
                        >
                          {i + 1}
                        </span>
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-s360-fg3">
                            {RANK_LABELS[i]} pick
                          </span>
                          <span className="text-sm font-semibold text-s360-fg1">
                            {d.distributor}
                          </span>
                          <span className="text-[11px] text-s360-fg3">
                            {d.availableCount} of {total} parts · {availPct}%
                            {d.unavailableCount > 0 && (
                              <span className="text-s360-danger font-semibold">
                                {" "}
                                · {d.unavailableCount} missing
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className="font-mono text-[13px] font-bold text-s360-ink tabular-nums">
                            {fmt(d.totalPrice)}
                          </span>
                          <span className="h-1.5 w-24 rounded-full bg-s360-bg2 overflow-hidden">
                            <span
                              className={`block h-full ${tone.bar}`}
                              style={{ width: `${availPct}%` }}
                            />
                          </span>
                          <button
                            disabled={d.unavailableCount > 0}
                            className={`inline-flex items-center gap-1 h-7 px-3 rounded-md text-xs font-semibold ${
                              i === 0
                                ? "bg-s360-violet text-white hover:bg-s360-violet-600"
                                : "bg-white border border-s360-stroke1 text-s360-fg1 hover:bg-s360-bg2"
                            } ${d.unavailableCount > 0 ? "opacity-60 cursor-not-allowed" : ""}`}
                          >
                            {i === 0 ? (
                              <>
                                <Check className="w-3 h-3" /> Select
                              </>
                            ) : (
                              "Select"
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Issues & alerts + quick actions */}
              <div className="bg-white rounded-md shadow-sm flex flex-col">
                <div className="px-[18px] py-3.5 border-b border-s360-stroke1">
                  <h3 className="text-base font-semibold text-s360-fg1 m-0">Issues &amp; alerts</h3>
                  <p className="text-xs text-s360-fg3 mt-0.5">
                    Things to review before placing the order
                  </p>
                </div>
                <div className="p-[18px] flex flex-col gap-2.5">
                  {totalUnavailable > 0 && (
                    <div className="flex gap-3 p-3 rounded-md bg-s360-danger-bg border-l-[3px] border-s360-danger">
                      <span className="w-8 h-8 rounded-lg bg-white/60 text-s360-danger inline-flex items-center justify-center shrink-0">
                        <AlertTriangle className="w-4 h-4" />
                      </span>
                      <div className="flex flex-col">
                        <span className="text-[13px] font-semibold text-[#700A14]">
                          {totalUnavailable} part-distributor pairs unavailable
                        </span>
                        <span className="text-xs text-[#700A14]/80">
                          Consider alternates or wait for restock.
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3 p-3 rounded-md bg-s360-info-bg border-l-[3px] border-s360-info">
                    <span className="w-8 h-8 rounded-lg bg-white/60 text-s360-info inline-flex items-center justify-center shrink-0">
                      <TrendingUp className="w-4 h-4" />
                    </span>
                    <div className="flex flex-col">
                      <span className="text-[13px] font-semibold text-[#002E4D]">Price trend alert</span>
                      <span className="text-xs text-[#002E4D]/80">
                        Monitor pricing for high-value components.
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 rounded-md bg-s360-success-bg border-l-[3px] border-s360-success">
                    <span className="w-8 h-8 rounded-lg bg-white/60 text-s360-success inline-flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4" />
                    </span>
                    <div className="flex flex-col">
                      <span className="text-[13px] font-semibold text-s360-success-ink">
                        All components verified
                      </span>
                      <span className="text-xs text-s360-success-ink/80">
                        No obsolete or NRND parts detected.
                      </span>
                    </div>
                  </div>
                </div>
                <div className="px-[18px] pb-[18px]">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-s360-fg3 mb-2">
                    Quick actions
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md bg-s360-violet text-white text-[13px] font-semibold hover:bg-s360-violet-600">
                      <ShoppingCart className="w-3.5 h-3.5" /> Generate purchase orders
                    </button>
                    <button className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2">
                      <BarChart3 className="w-3.5 h-3.5" /> Optimize for cost
                    </button>
                    <button className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2">
                      <Clock className="w-3.5 h-3.5" /> Optimize for speed
                    </button>
                    <button className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2">
                      <Users className="w-3.5 h-3.5" /> Export report
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </PageWrapper>
  );
};

export default BomAnalysisPage;
